/**
 * Stage label resolver — single source of truth for stage-aware labels
 * across the web app + extension (popup + content scripts).
 *
 * The stage is read at build time from `import.meta.env.VITE_APP_STAGE`
 * (injected by `vite.config.ts`). Three valid values: `local`, `dev`,
 * `prod`. Anything else (including unset) falls back to `dev` so a
 * misresolved stage can never accidentally show "no badge" (which is
 * the prod affordance) and obscure the targeting from the recruiter.
 *
 * The resolver is a pure function — it does not consult sessionStorage,
 * does not read URL params, does not depend on React. Web and extension
 * both import it and consume the same `StageLabel` shape so the
 * displayed text format can never drift between surfaces.
 *
 * Pairing: `<StageBadge>` in `@nu-assist/ui` consumes `getStageLabel()`
 * via a `stage` prop, keeping `ui` free of `import.meta.env` reads (a
 * "pure design system" invariant).
 */

export type Stage = "local" | "dev" | "prod";

export interface StageLabel {
  /** Stage identifier (lowercase). */
  stage: Stage;
  /**
   * Short uppercase label for the StageBadge pill. Empty string on prod
   * (the badge renders `null` in that case, but a consumer reading this
   * field directly gets a stable, safe-to-concat value).
   */
  badgeLabel: string;
  /**
   * Whether to render the StageBadge at all. False on prod — prod is
   * the silent default. True on local + dev.
   */
  showBadge: boolean;
  /**
   * Parenthetical suffix for the NUCCE label across the popup +
   * injected Thrive panels. `" (LOCAL)"` on local, `" (DEV)"` on dev,
   * empty string on prod (so a consumer can unconditionally write
   * `` `NUCCE${parenthetical}` `` and the prod output stays "NUCCE").
   *
   * Leading space is INCLUDED so the caller doesn't have to special-case
   * the prod-empty branch in their JSX.
   */
  nucceParenthetical: string;
}

/** Canonical product name with the accent dot, as the brief specifies. */
export const PRODUCT_NAME = "Nu Assist.";

/**
 * Read the active stage from the build-time env. Defaults to `dev` on
 * any unrecognised value — see module docstring for why.
 */
export function resolveStage(): Stage {
  // `import.meta.env.VITE_APP_STAGE` is replaced by Vite at build time
  // with a string literal; `?? ""` handles the test/Node case where it
  // may be undefined (vitest test setup).
  const raw = (import.meta.env.VITE_APP_STAGE ?? "").toString().toLowerCase();
  if (raw === "prod") return "prod";
  if (raw === "local") return "local";
  return "dev";
}

/**
 * Compute the canonical `StageLabel` for the given stage. The default
 * argument calls `resolveStage()` so consumers in production code can
 * just write `getStageLabel()`; tests pass an explicit stage to avoid
 * env-stubbing per assertion.
 */
export function getStageLabel(stage: Stage = resolveStage()): StageLabel {
  if (stage === "prod") {
    return {
      stage,
      badgeLabel: "",
      showBadge: false,
      nucceParenthetical: "",
    };
  }
  const label = stage === "local" ? "LOCAL" : "DEV";
  return {
    stage,
    badgeLabel: label,
    showBadge: true,
    nucceParenthetical: ` (${label})`,
  };
}
